import { useState, useEffect } from "react";
import { Download, UploadCloud, Loader2, Edit2, Save, X, AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { db } from "../firebase/config";
import { collection, getDocs, query, doc, updateDoc, deleteDoc, setDoc } from "firebase/firestore";
import { getAuth, onAuthStateChanged, IdTokenResult } from "firebase/auth";
import { useToast } from "@/hooks/use-toast";

// === Types ===
interface FeeCategory {
  name: string;
  term1: number;
  term2: number;
  term3: number;
  paidOnce?: boolean;
  daySchoolTerm1?: number;
  daySchoolTerm2?: number;
  daySchoolTerm3?: number;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface FeeStructure {
  feeType: string;
  classes: string[];
  categories: FeeCategory[];
  insights?: string;
  importantNotes?: string;
  feeStructureUid?: string;
  createdAt?: any;
  submittedBy?: string;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

interface LevelFees {
  level: string;
  categories: FeeCategory[];
  classes: string[];
  feeType?: string;
  isGovernmentSponsored?: boolean;
  governmentSponsorshipAmount?: number;
  feeStructureUid?: string;
  insights?: string;
  importantNotes?: string;
  termIdsByClass?: Record<string, { term1: number; term2: number; term3: number }>;
}

// === Helpers ===
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 0,
  }).format(amount);

const calculateTermTotal = (
  categories: FeeCategory[],
  term: "term1" | "term2" | "term3",
  feeType: "boarding" | "day" = "boarding"
) => {
  if (feeType === "day") {
    const dayTermMap = { term1: "daySchoolTerm1", term2: "daySchoolTerm2", term3: "daySchoolTerm3" };
    return categories.reduce((sum, c) => sum + (c[dayTermMap[term] as keyof FeeCategory] as number || 0), 0);
  }
  return categories.reduce((sum, c) => sum + c[term], 0);
};

// === PDF Generator (CDN-loaded) ===
const generatePDF = async (
  levelFeesData: LevelFees[], 
  insights?: string, 
  importantNotes?: string,
  schoolDetails?: {name: string; address: string; email: string; logoUrl: string; motto?: string} | null
) => {
  const loadScript = (src: string) =>
    new Promise<void>((resolve, reject) => {
      // Check if already loaded
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        resolve();
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
      document.head.appendChild(script);
    });

  try {
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js");
    
    // Wait for scripts to initialize and check multiple locations
    await new Promise(resolve => setTimeout(resolve, 200));
    
    let jsPDF = (window as any).jsPDF;
    
    // Check different possible locations where jsPDF might be exposed
    if (!jsPDF && (window as any).jspdf) {
      jsPDF = (window as any).jspdf.jsPDF;
    }
    
    if (!jsPDF) {
      console.error("Available window properties:", Object.keys(window).filter(k => k.toLowerCase().includes('pdf')));
      throw new Error("jsPDF library not loaded. Please check your internet connection and try again.");
    }
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;

    const addHeader = async () => {
      // Professional header with school details
      let currentY = 8; // Back to original position
      
      // Add logo if available - positioned to align with text
      if (schoolDetails?.logoUrl) {
        try {
          const img = await new Promise<HTMLImageElement>((resolve, reject) => {
            const image = new Image();
            image.crossOrigin = "anonymous";
            image.onload = () => resolve(image);
            image.onerror = reject;
            image.src = schoolDetails.logoUrl;
          });
          
          // Logo moved significantly closer to center - cutting distance to schoolname by half
          const logoSize = 25;
          const centerX = pageWidth / 2;
          const logoX = centerX - 45 - (logoSize * 0.7); // Move left by 70% of logo width
          doc.addImage(img, 'PNG', logoX, currentY, logoSize, logoSize);
        } catch (error) {
          console.error("Failed to load logo:", error);
        }
      }
      
      // School name - bold and prominent (UNCHANGED from original)
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(37, 99, 235);
      doc.text(schoolDetails?.name || "School Name", pageWidth / 2, currentY + 4, { align: "center" });
      currentY += 9;
      
      // School address
      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      if (schoolDetails?.address) {
        doc.text(`Address: ${schoolDetails.address}`, pageWidth / 2, currentY, { align: "center" });
        currentY += 5;
      }
      
      // School email
      if (schoolDetails?.email) {
        doc.text(`Email: ${schoolDetails.email}`, pageWidth / 2, currentY, { align: "center" });
        currentY += 5;
      }
      
      // School motto
      if (schoolDetails?.motto) {
        doc.setFont("helvetica", "italic");
        doc.setFontSize(8);
        doc.text(`Motto: "${schoolDetails.motto}"`, pageWidth / 2, currentY, { align: "center" });
        currentY += 5;
      }
      
      // Separator line
      currentY += 2;
      doc.setDrawColor(200, 200, 200);
      doc.line(10, currentY, pageWidth - 10, currentY);
      currentY += 2;
      
      // Document title
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text("Fee Structure - Academic Year 2024/2025", pageWidth / 2, currentY + 4, { align: "center" });
      
      return currentY + 8;
    };
    
    const initialHeaderHeight = await addHeader();

    let y = initialHeaderHeight + 5;
    for (let i = 0; i < levelFeesData.length; i++) {
      const lvl = levelFeesData[i];
      if (i > 0) {
        doc.addPage();
        await addHeader();
        y = 40;
      }

      // Handle long class lists by using autoTable for robust word wrapping
      const levelText = `${lvl.level} Level`;
      (doc as any).autoTable({
        startY: y,
        body: [[levelText]],
        theme: 'plain',
        styles: {
          font: 'helvetica',
          fontStyle: 'bold',
fontSize: 14,
          fillColor: [248, 250, 252],
          textColor: [37, 99, 235],
          cellPadding: { top: 4, right: 4, bottom: 4, left: 4 },
        },
        margin: { left: 10, right: 10 },
      });
      y = (doc as any).lastAutoTable.finalY + 6;

      // Format currency with proper spacing: KSh 150,000
      const formatPDFCurrency = (amount: number) => {
        if (amount === 0) return "-";
        return "KSh " + amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
      };

      const body = lvl.categories.map((c) => [
        c.paidOnce ? `${c.name} *` : c.name,
        formatPDFCurrency(c.term1),
        formatPDFCurrency(c.term2),
        formatPDFCurrency(c.term3),
      ]);

      const t1 = calculateTermTotal(lvl.categories, "term1");
      const t2 = calculateTermTotal(lvl.categories, "term2");
      const t3 = calculateTermTotal(lvl.categories, "term3");
      const annualTotal = t1 + t2 + t3;
      
      body.push(
        ["TERM TOTAL", formatPDFCurrency(t1), formatPDFCurrency(t2), formatPDFCurrency(t3)],
        ["ANNUAL TOTAL", formatPDFCurrency(annualTotal), "-", "-"]
      );

      // Add government sponsorship rows if applicable
      if (lvl.isGovernmentSponsored && lvl.governmentSponsorshipAmount && lvl.governmentSponsorshipAmount > 0) {
        body.push(
          ["GOVERNMENT SPONSORSHIP", formatPDFCurrency(lvl.governmentSponsorshipAmount), "-", "-"],
          ["NET AMOUNT PAYABLE", formatPDFCurrency(Math.max(0, annualTotal - lvl.governmentSponsorshipAmount)), "-", "-"]
        );
      }

      (doc as any).autoTable({
        startY: y,
        head: [["Fee Category", "Term 1", "Term 2", "Term 3"]],
        body,
        theme: "striped",
        headStyles: { fillColor: [37, 99, 235], textColor: [255, 255, 255], fontStyle: "bold", halign: "center" },
        styles: { fontSize: 9, cellPadding: 4, overflow: 'linebreak', cellWidth: 'wrap' },
        columnStyles: {
          0: { cellWidth: 'auto', halign: "left", overflow: 'linebreak' },
          1: { cellWidth: 40, halign: "center" },
          2: { cellWidth: 40, halign: "center" },
          3: { cellWidth: 40, halign: "center" },
        },
        margin: { left: 14, right: 14 },
      });
      y = (doc as any).lastAutoTable.finalY + 10;

      // Add uniform fee note if applicable
      if (lvl.feeType === "uniform") {
        doc.setFontSize(9);
        doc.setTextColor(37, 99, 235);
        doc.setFont("helvetica", "italic");
        doc.text("Note: This fee structure applies uniformly to all students across all classes and streams.", 14, y);
        y += 10;
      }
    }

    // Add insights and notes on a new page
    doc.addPage();
    await addHeader();

    let notesY = 45;

    // Fee Structure Insights
    doc.setFillColor(248, 250, 252);
    doc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Fee Structure Insights", 18, notesY + 5);
    notesY += 15;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");

    // Use provided insights or default text
    const insightLines = insights 
      ? insights.split('\n').map(line => line.trim()).filter(line => line)
      : [
          "• Admission fees are one-time payments collected only in Term 1, making the first",
          "  term's total higher than subsequent terms.",
          "",
          "• Recurring termly fees remain consistent across Term 2 and Term 3, providing",
          "  predictable budgeting for families after the initial term.",
          "",
          "• Fees increase progressively by level to reflect enhanced facilities, specialized",
          "  instruction, and additional examination requirements at higher academic levels."
        ];

    insightLines.forEach(line => {
      doc.text(line, 18, notesY);
      notesY += 5;
    });

    notesY += 10;

    // Important Notes
    doc.setFillColor(248, 250, 252);
    doc.rect(14, notesY - 2, pageWidth - 28, 10, "F");
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.text("Important Notes", 18, notesY + 5);
    notesY += 15;

    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    doc.setFont("helvetica", "normal");

    // Use provided notes or default text
    const noteLines = importantNotes
      ? importantNotes.split('\n').map(line => line.trim()).filter(line => line)
      : [
          "• All fees are quoted in Kenyan Shillings (KSH) and are subject to review annually.",
          "",
          "• Payment plans are available for families requiring flexible payment options.",
          "",
          "• Boarding fees include accommodation, meals, and supervision services.",
          "",
          "• Transport fees vary based on distance; rates shown are for standard routes.",
          "",
          "• Admission fees are one-time charges applicable only in Term 1 of the academic year."
        ];

    noteLines.forEach(line => {
      doc.text(line, 18, notesY);
      notesY += 5;
    });

    doc.save("School-Fee-Structure-2024-2025.pdf");
  } catch (error) {
    console.error("PDF generation failed:", error);
    alert("Failed to generate PDF. Please try again.");
  }
};

// Content-only component for embedding
export default function PreviewFeeStructure() {
  const { toast } = useToast();
  const [levelFeesData, setLevelFeesData] = useState<LevelFees[]>([]);
  const [loading, setLoading] = useState(true);
  const [schoolID, setSchoolID] = useState<string | null>(null);
  const [schoolDetails, setSchoolDetails] = useState<{name: string; address: string; email: string; logoUrl: string; motto?: string} | null>(null);
  const [schoolType, setSchoolType] = useState<string>("");
  const [feeTypeView, setFeeTypeView] = useState<"boarding" | "day">("boarding");
  const [globalInsights, setGlobalInsights] = useState<string>("");
  const [globalNotes, setGlobalNotes] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingData, setEditingData] = useState<LevelFees | null>(null);
  const [saving, setSaving] = useState(false);
  const [classesWithoutFees, setClassesWithoutFees] = useState<string[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [includeGovernmentAmount, setIncludeGovernmentAmount] = useState(false);
  /* ── Bullet List Edit State ───────────────────────────── */
  const [editingInsightsList, setEditingInsightsList] = useState<string[]>([]);
  const [currentEditingInsight, setCurrentEditingInsight] = useState("");
  const [editingNotesList, setEditingNotesList] = useState<string[]>([]);
  const [currentEditingNote, setCurrentEditingNote] = useState("");
  
  // Inline editing state for individual fee structure
  const [editingInsightIndex, setEditingInsightIndex] = useState<number | null>(null);
  const [editingInsightValue, setEditingInsightValue] = useState("");
  const [editingNoteIndex, setEditingNoteIndex] = useState<number | null>(null);
  const [editingNoteValue, setEditingNoteValue] = useState("");

  const addEditingInsight = () => {
    if (currentEditingInsight.trim()) {
      setEditingInsightsList([...editingInsightsList, currentEditingInsight.trim()]);
      setCurrentEditingInsight("");
    }
  };

  const removeEditingInsight = (index: number) => {
    setEditingInsightsList(editingInsightsList.filter((_, i) => i !== index));
  };

  const startEditInsight = (index: number, value: string) => {
    setEditingInsightIndex(index);
    setEditingInsightValue(value);
  };

  const saveEditInsight = () => {
    if (editingInsightIndex !== null && editingInsightValue.trim()) {
      const updated = [...editingInsightsList];
      updated[editingInsightIndex] = editingInsightValue.trim();
      setEditingInsightsList(updated);
    }
    setEditingInsightIndex(null);
    setEditingInsightValue("");
  };

  const cancelEditInsight = () => {
    setEditingInsightIndex(null);
    setEditingInsightValue("");
  };

  const addEditingNote = () => {
    if (currentEditingNote.trim()) {
      setEditingNotesList([...editingNotesList, currentEditingNote.trim()]);
      setCurrentEditingNote("");
    }
  };

  const removeEditingNote = (index: number) => {
    setEditingNotesList(editingNotesList.filter((_, i) => i !== index));
  };

  const startEditNote = (index: number, value: string) => {
    setEditingNoteIndex(index);
    setEditingNoteValue(value);
  };

  const saveEditNote = () => {
    if (editingNoteIndex !== null && editingNoteValue.trim()) {
      const updated = [...editingNotesList];
      updated[editingNoteIndex] = editingNoteValue.trim();
      setEditingNotesList(updated);
    }
    setEditingNoteIndex(null);
    setEditingNoteValue("");
  };

  const cancelEditNote = () => {
    setEditingNoteIndex(null);
    setEditingNoteValue("");
  };

  const [savingGlobalNotes, setSavingGlobalNotes] = useState(false);

  /* ── Global Bullet List Edit State ────────────────────── */
  const [editingGlobalInsightsList, setEditingGlobalInsightsList] = useState<string[]>([]);
  const [currentGlobalInsight, setCurrentGlobalInsight] = useState("");
  const [editingGlobalNotesList, setEditingGlobalNotesList] = useState<string[]>([]);
  const [currentGlobalNote, setCurrentGlobalNote] = useState("");
  const [isEditingGlobalNotes, setIsEditingGlobalNotes] = useState(false);

  // Inline editing state for global insights/notes
  const [editingGlobalInsightIndex, setEditingGlobalInsightIndex] = useState<number | null>(null);
  const [editingGlobalInsightValue, setEditingGlobalInsightValue] = useState("");
  const [editingGlobalNoteIndex, setEditingGlobalNoteIndex] = useState<number | null>(null);
  const [editingGlobalNoteValue, setEditingGlobalNoteValue] = useState("");

  const addGlobalInsight = () => {
      if (currentGlobalInsight.trim()) {
          setEditingGlobalInsightsList([...editingGlobalInsightsList, currentGlobalInsight.trim()]);
          setCurrentGlobalInsight("");
      }
  };

  const removeGlobalInsight = (index: number) => {
      setEditingGlobalInsightsList(editingGlobalInsightsList.filter((_, i) => i !== index));
  };

  const startEditGlobalInsight = (index: number, value: string) => {
    setEditingGlobalInsightIndex(index);
    setEditingGlobalInsightValue(value);
  };

  const saveEditGlobalInsight = () => {
    if (editingGlobalInsightIndex !== null && editingGlobalInsightValue.trim()) {
      const updated = [...editingGlobalInsightsList];
      updated[editingGlobalInsightIndex] = editingGlobalInsightValue.trim();
      setEditingGlobalInsightsList(updated);
    }
    setEditingGlobalInsightIndex(null);
    setEditingGlobalInsightValue("");
  };

  const cancelEditGlobalInsight = () => {
    setEditingGlobalInsightIndex(null);
    setEditingGlobalInsightValue("");
  };

  const addGlobalNote = () => {
      if (currentGlobalNote.trim()) {
          setEditingGlobalNotesList([...editingGlobalNotesList, currentGlobalNote.trim()]);
          setCurrentGlobalNote("");
      }
  };

  const removeGlobalNote = (index: number) => {
      setEditingGlobalNotesList(editingGlobalNotesList.filter((_, i) => i !== index));
  };

  const startEditGlobalNote = (index: number, value: string) => {
    setEditingGlobalNoteIndex(index);
    setEditingGlobalNoteValue(value);
  };

  const saveEditGlobalNote = () => {
    if (editingGlobalNoteIndex !== null && editingGlobalNoteValue.trim()) {
      const updated = [...editingGlobalNotesList];
      updated[editingGlobalNoteIndex] = editingGlobalNoteValue.trim();
      setEditingGlobalNotesList(updated);
    }
    setEditingGlobalNoteIndex(null);
    setEditingGlobalNoteValue("");
  };

  const cancelEditGlobalNote = () => {
    setEditingGlobalNoteIndex(null);
    setEditingGlobalNoteValue("");
  };

  const handleEditGlobalNotes = () => {
    // Parse global strings
    const parse = (text: string) => text.split('\n').filter(l => l.trim()).map(l => l.trim().replace(/^•\s*/, ''));
    setEditingGlobalInsightsList(parse(globalInsights));
    setEditingGlobalNotesList(parse(globalNotes));
    setIsEditingGlobalNotes(true);
  };

  const handleCancelGlobalNotesEdit = () => {
    setIsEditingGlobalNotes(false);
    setEditingGlobalInsightsList([]);
    setEditingGlobalNotesList([]);
  };

  const handleSaveGlobalNotes = async () => {
      if (!schoolID) return;
      setSavingGlobalNotes(true);
      try {
          const finalInsights = editingGlobalInsightsList.map(i => `• ${i}`).join('\n');
          const finalNotes = editingGlobalNotesList.map(n => `• ${n}`).join('\n');

          // Update ALL fee structures
          const q = query(collection(db, `schools/${schoolID}/feeStructures`));
          const snap = await getDocs(q);
          const updates = snap.docs.map(d => updateDoc(d.ref, {
              insights: finalInsights,
              importantNotes: finalNotes
          }));
          await Promise.all(updates);
          
          setGlobalInsights(finalInsights);
          setGlobalNotes(finalNotes);
          setIsEditingGlobalNotes(false);
          toast({ title: "Updated Global Details", description: "Applied to all fee structures." });
      } catch (e) {
          console.error(e);
          toast({ title: "Error", description: "Failed to update global details.", variant: "destructive" });
      } finally {
          setSavingGlobalNotes(false);
      }
  };

  // Helper function to extract base class name without stream
  const extractBaseClassName = (className: string): string => {
    // Remove stream names like "Red", "Blue", "Green", etc.
    const parts = className.split(" ");
    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1];
      // Check if last part is a stream name (case-insensitive)
      const streamNames = ["red", "blue", "green", "yellow", "pink", "purple", "orange", "north", "south", "east", "west", "a", "b", "c", "d"];
      if (streamNames.includes(lastPart.toLowerCase())) {
        return parts.slice(0, -1).join(" ");
      }
    }
    return className;
  };

  // Fetch school ID from auth claims
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult: IdTokenResult = await user.getIdTokenResult(true);
          const affiliations = (idTokenResult.claims.affiliations as Affiliation[] | undefined) || [];
          const defaultAffiliation = idTokenResult.claims.defaultAffiliation as Affiliation | undefined;
          const claimSchoolID = idTokenResult.claims.schoolID as string | undefined;

          let selectedAff: Affiliation | null = null;
          if (defaultAffiliation?.role === "admin" && defaultAffiliation.schoolID) {
            selectedAff = defaultAffiliation;
          } else if (affiliations.length > 0) {
            selectedAff = affiliations.find((a) => a.role === "admin" && a.schoolID) || affiliations[0];
          }

          if (selectedAff?.schoolID) {
            setSchoolID(selectedAff.schoolID);
          } else if (claimSchoolID) {
            setSchoolID(claimSchoolID);
          }
        } catch (err) {
          console.error("Error fetching school ID:", err);
        }
      }
    });

    return () => unsubscribe();
  }, []);

  // Fetch school details
  useEffect(() => {
    if (!schoolID) return;

    const fetchSchoolDetails = async () => {
      try {
        const schoolDoc = await getDocs(query(collection(db, `schools/${schoolID}/schoolDetails`)));
        if (!schoolDoc.empty) {
          const data = schoolDoc.docs[0].data();
          setSchoolDetails({
            name: data.name || "",
            address: data.address || "",
            email: data.email || "",
            logoUrl: data.logoUrl || "",
            motto: data.motto || "",
          });
          setSchoolType(data.type || "");
        }
      } catch (error) {
        console.error("Error fetching school details:", error);
      }
    };

    fetchSchoolDetails();
  }, [schoolID]);

  // Fetch fee structures from Firebase
  useEffect(() => {
    if (!schoolID) return;

    const fetchFeeStructures = async () => {
      setLoading(true);
      try {
        // Fetch all classes first
        const classesQuery = query(collection(db, `schools/${schoolID}/classes`));
        const classesSnapshot = await getDocs(classesQuery);
        const allClassNames = classesSnapshot.docs.map((d) => d.data().name as string).sort();
        
        console.log("All class names from DB:", allClassNames);
        
        // Extract base class names (without streams)
        const baseClassNames = [...new Set(
          allClassNames.map(cls => extractBaseClassName(cls).trim())
        )];
        
        console.log("Extracted base class names:", baseClassNames);
        
        // Store all base class names for warning card comparison
        const allBaseClasses = baseClassNames;

        // Fetch fee structures
        const q = query(collection(db, `schools/${schoolID}/feeStructures`));
        const querySnapshot = await getDocs(q);
        
        const feeStructures: FeeStructure[] = [];
        querySnapshot.forEach((doc) => {
          const data = { ...doc.data(), feeStructureUid: doc.id } as FeeStructure;
          console.log("Loaded fee structure:", data);
          feeStructures.push(data);
        });

        if (feeStructures.length === 0) {
          toast({
            title: "No Fee Structures",
            description: "No fee structures have been created yet. Please create one first.",
            variant: "destructive",
          });
          setLevelFeesData([]);
          setClassesWithoutFees(allBaseClasses);
          setLoading(false);
          return;
        }

        // Group fee structures by classes and merge categories
        const groupedData: LevelFees[] = [];
        const allInsights: string[] = [];
        const allNotes: string[] = [];
        const classesWithFees = new Set<string>();

        feeStructures.forEach((structure) => {
          // Collect all insights and notes (not just from first structure)
          if (structure.insights && structure.insights.trim()) {
            allInsights.push(structure.insights.trim());
          }
          if (structure.importantNotes && structure.importantNotes.trim()) {
            allNotes.push(structure.importantNotes.trim());
          }

          let classLabel: string;
          
          // If uniform fee type, show official message
          if (structure.feeType === "uniform") {
            classLabel = "All Classes";
            // Mark all classes as having fees
            allBaseClasses.forEach(cls => classesWithFees.add(cls));
          } else {
            // Classes are already stored as base class names in the database
            // No need to extract again, just use them directly
            const classesForStructure = structure.classes || [];
            console.log("Classes from fee structure:", classesForStructure);
            classesForStructure.forEach(cls => {
              // Normalize by trimming whitespace
              const normalizedClass = cls.trim();
              classesWithFees.add(normalizedClass);
              console.log("Added to classesWithFees:", normalizedClass);
            });
            classLabel = classesForStructure.length > 0 
              ? classesForStructure.join(", ") 
              : "General";
          }

          groupedData.push({
            level: classLabel,
            categories: structure.categories,
            classes: structure.classes,
            feeType: structure.feeType,
            isGovernmentSponsored: structure.isGovernmentSponsored,
            governmentSponsorshipAmount: structure.governmentSponsorshipAmount || 0,
            feeStructureUid: structure.feeStructureUid,
            insights: structure.insights,
            importantNotes: structure.importantNotes,
            termIdsByClass: structure.termIdsByClass,
          });
        });

        // Find classes without fees
        console.log("All base classes:", allBaseClasses);
        console.log("Classes with fees:", Array.from(classesWithFees));
        
        // Compare with normalized strings
        const missingClasses = allBaseClasses.filter(cls => {
          const normalizedCls = cls.trim();
          const hasFee = classesWithFees.has(normalizedCls);
          if (!hasFee) {
            console.log(`Class "${normalizedCls}" not found in classesWithFees`);
          }
          return !hasFee;
        });
        
        console.log("Missing classes:", missingClasses);
        setClassesWithoutFees(missingClasses);

        setLevelFeesData(groupedData);
        // Combine all insights and notes from all structures
        setGlobalInsights(allInsights.join("\n\n"));
        setGlobalNotes(allNotes.join("\n\n"));
        
        console.log("Global insights set to:", allInsights.join("\n\n"));
        console.log("Global notes set to:", allNotes.join("\n\n"));
      } catch (error) {
        console.error("Error fetching fee structures:", error);
        toast({
          title: "Error",
          description: "Failed to load fee structures from database.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFeeStructures();
  }, [schoolID, toast]);

  const handleEdit = (level: LevelFees) => {
    setEditingData(JSON.parse(JSON.stringify(level))); // Deep clone
    
    // Parse existing insights/notes into lists
    // Expected format: "• Item 1\n• Item 2" OR raw text
    const parseBulletString = (text?: string) => {
        if (!text) return [];
        return text.split('\n')
            .map(line => line.trim().replace(/^•\s*/, '')) // Remove leading bullet and space
            .filter(line => line.length > 0);
    };

    setEditingInsightsList(parseBulletString(level.insights));
    setEditingNotesList(parseBulletString(level.importantNotes));
    
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditingData(null);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!editingData || !editingData.feeStructureUid || !schoolID) return;

    setSaving(true);
    try {
      const docRef = doc(db, `schools/${schoolID}/feeStructures`, editingData.feeStructureUid);
      
      // Build update object without undefined values
      const updateData: any = {
        categories: editingData.categories,
        isGovernmentSponsored: editingData.isGovernmentSponsored,
        governmentSponsorshipAmount: editingData.governmentSponsorshipAmount || 0,
      };

      // Process Insights and Notes lists into final strings
      const finalInsights = editingInsightsList.length > 0
        ? editingInsightsList.map(i => `• ${i}`).join('\n')
        : (editingData.insights || "").trim();

      const finalNotes = editingNotesList.length > 0
        ? editingNotesList.map(n => `• ${n}`).join('\n')
        : (editingData.importantNotes || "").trim();

      if (finalInsights) {
        updateData.insights = finalInsights;
      } else {
        updateData.insights = "";
      }

      if (finalNotes) {
        updateData.importantNotes = finalNotes;
      } else {
        updateData.importantNotes = ""; // Clear if empty
      }

      await updateDoc(docRef, updateData);

      toast({
        title: "Success",
        description: "Fee structure updated successfully",
      });

      // Refresh data
      const q = query(collection(db, `schools/${schoolID}/feeStructures`));
      const querySnapshot = await getDocs(q);
      
      const feeStructures: FeeStructure[] = [];
      querySnapshot.forEach((doc) => {
        feeStructures.push({ ...doc.data(), feeStructureUid: doc.id } as FeeStructure);
      });

      const groupedData: LevelFees[] = [];
      const allInsights: string[] = [];
      const allNotes: string[] = [];

      feeStructures.forEach((structure) => {
        // Collect all insights and notes
        if (structure.insights && structure.insights.trim()) {
          allInsights.push(structure.insights.trim());
        }
        if (structure.importantNotes && structure.importantNotes.trim()) {
          allNotes.push(structure.importantNotes.trim());
        }

        let classLabel: string;
        
        if (structure.feeType === "uniform") {
          classLabel = "All Classes";
        } else {
          // Classes are already stored as base class names
          const classesForStructure = structure.classes || [];
          classLabel = classesForStructure.length > 0 
            ? classesForStructure.join(", ") 
            : "General";
        }

        groupedData.push({
          level: classLabel,
          categories: structure.categories,
          classes: structure.classes,
          feeType: structure.feeType,
          isGovernmentSponsored: structure.isGovernmentSponsored,
          governmentSponsorshipAmount: structure.governmentSponsorshipAmount || 0,
          feeStructureUid: structure.feeStructureUid,
          insights: structure.insights,
          importantNotes: structure.importantNotes,
        });
      });

      setLevelFeesData(groupedData);
      setGlobalInsights(allInsights.join("\n\n"));
      setGlobalNotes(allNotes.join("\n\n"));
      setIsEditing(false);
      setEditingData(null);
    } catch (error) {
      console.error("Error updating fee structure:", error);
      toast({
        title: "Error",
        description: "Failed to update fee structure",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const updateCategoryField = (index: number, field: keyof FeeCategory, value: string | number | boolean) => {
    if (!editingData) return;
    const updated = [...editingData.categories];
    if (field === "paidOnce") {
      updated[index][field] = value as boolean;
    } else if (field === "name") {
      updated[index][field] = value as string;
    } else {
      // Handle all numeric fields including day school fees
      (updated[index] as any)[field] = Number(value) || 0;
    }
    setEditingData({ ...editingData, categories: updated });
  };

  const handleDelete = async (level: LevelFees) => {
    if (!level.feeStructureUid || !schoolID) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete the fee structure for "${level.level}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setDeletingId(level.feeStructureUid);
    try {
      const docRef = doc(db, `schools/${schoolID}/feeStructures`, level.feeStructureUid);
      await deleteDoc(docRef);

      toast({
        title: "Success",
        description: "Fee structure deleted successfully",
      });

      // Refresh data
      const q = query(collection(db, `schools/${schoolID}/feeStructures`));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setLevelFeesData([]);
        setGlobalInsights("");
        setGlobalNotes("");
        setLoading(false);
        return;
      }

      const feeStructures: FeeStructure[] = [];
      querySnapshot.forEach((doc) => {
        feeStructures.push({ ...doc.data(), feeStructureUid: doc.id } as FeeStructure);
      });

      const groupedData: LevelFees[] = [];
      let firstInsights = "";
      let firstNotes = "";

      feeStructures.forEach((structure, index) => {
        if (index === 0) {
          firstInsights = structure.insights || "";
          firstNotes = structure.importantNotes || "";
        }

        let classLabel: string;
        
        if (structure.feeType === "uniform") {
          classLabel = "All Classes";
        } else {
          const baseClassNames = [...new Set(
            structure.classes.map(cls => extractBaseClassName(cls))
          )];
          classLabel = baseClassNames.length > 0 
            ? baseClassNames.join(", ") 
            : "General";
        }

        groupedData.push({
          level: classLabel,
          categories: structure.categories,
          classes: structure.classes,
          feeType: structure.feeType,
          isGovernmentSponsored: structure.isGovernmentSponsored,
          governmentSponsorshipAmount: structure.governmentSponsorshipAmount || 0,
          feeStructureUid: structure.feeStructureUid,
          insights: structure.insights,
          importantNotes: structure.importantNotes,
        });
      });

      setLevelFeesData(groupedData);
      setGlobalInsights(firstInsights);
      setGlobalNotes(firstNotes);
    } catch (error) {
      console.error("Error deleting fee structure:", error);
      toast({
        title: "Error",
        description: "Failed to delete fee structure",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
      setDeletingId(null);
    }
  };

  const handleDeploy = async () => {
    if (!schoolID || levelFeesData.length === 0) {
      toast({
        title: "Error",
        description: "No fee structures to deploy",
        variant: "destructive",
      });
      return;
    }

    setDeploying(true);
    try {
      // Use fixed document ID to overwrite existing deployment
      const deployedRef = doc(db, `schools/${schoolID}/deployedFeeStructures`, 'current');
      
      // Collect all termIdsByClass from all fee structures into one object
      const allTermIdsByClass: Record<string, { term1: number; term2: number; term3: number }> = {};
      levelFeesData.forEach(lvl => {
        if (lvl.termIdsByClass) {
          Object.entries(lvl.termIdsByClass).forEach(([cls, termIds]) => {
            allTermIdsByClass[cls] = termIds;
          });
        }
      });

      // Prepare data for deployment - always include full government data
      const deployData: any = {
        levelFees: levelFeesData.map(lvl => ({
          level: lvl.level,
          categories: lvl.categories,
          classes: lvl.classes,
          feeType: lvl.feeType,
          isGovernmentSponsored: lvl.isGovernmentSponsored,
          governmentSponsorshipAmount: lvl.governmentSponsorshipAmount || 0,
          termIdsByClass: lvl.termIdsByClass || {},
        })),
        termIdsByClass: allTermIdsByClass, // All term IDs at root level for easy access
        deployedAt: new Date(),
        showGovernmentAmount: includeGovernmentAmount,
      };

      // Only add insights and notes if they exist
      if (globalInsights && globalInsights.trim()) {
        deployData.insights = globalInsights.trim();
      }
      if (globalNotes && globalNotes.trim()) {
        deployData.importantNotes = globalNotes.trim();
      }

      await setDoc(deployedRef, deployData);

      toast({
        title: "Success",
        description: "Fee structure deployed successfully! Parents can now view it.",
      });
    } catch (error) {
      console.error("Error deploying fee structure:", error);
      toast({
        title: "Error",
        description: "Failed to deploy fee structure",
        variant: "destructive",
      });
    } finally {
      setDeploying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600 dark:text-gray-300">Loading fee structures...</span>
      </div>
    );
  }

  if (levelFeesData.length === 0) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
          <CardContent className="p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-6">
                <svg className="h-16 w-16 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No Fee Structure Found</h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  No fee structures have been created yet.
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Switch to the <span className="font-semibold">"Create"</span> tab to set up your first fee structure.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 m-2">
        <div className="space-y-2">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            School Fee Structure
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300">Academic Year 2024/2025</p>
        </div>
        <Button 
          onClick={() => generatePDF(levelFeesData, globalInsights, globalNotes, schoolDetails)} 
          size="lg" 
          className="gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
        >
          <Download className="h-5 w-5" />
          Download PDF
        </Button>
      </div>

      {classesWithoutFees.length > 0 && (
        <Card className="m-2 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 rounded-lg shadow">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-200 mb-2">
                  Missing Fee Structures
                </h3>
                <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                  The following classes do not have fee structures assigned. Please create fee structures for these classes to avoid confusion:
                </p>
                <div className="flex flex-wrap gap-2">
                  {classesWithoutFees.map((className) => (
                    <Badge 
                      key={className} 
                      variant="outline" 
                      className="border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 bg-white dark:bg-red-950"
                    >
                      {className}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
        <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">Fee Breakdown by Term</CardTitle>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {levelFeesData.length > 0 && levelFeesData[0].feeType === "uniform" 
                ? "Fee structure applies uniformly across all classes and streams" 
                : "View all fee structures for different class levels"}
            </p>
          </div>
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg">
              <Label htmlFor="include-govt-amount" className="text-xs font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                Show Government Paid Amount
              </Label>
              <Switch
                id="include-govt-amount"
                checked={includeGovernmentAmount}
                onCheckedChange={setIncludeGovernmentAmount}
              />
            </div>
            <Button 
              onClick={handleDeploy}
              disabled={deploying}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
            >
              {deploying ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Deploying...
                </>
              ) : (
                <>
                  <UploadCloud className="h-5 w-5" />
                  Publish
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Fee Type Navbar - Only show if school is Mixed */}
          {schoolType === "Mixed (Day & Boarding)" && (
            <div className="mb-6">
              <Tabs value={feeTypeView} onValueChange={(value) => setFeeTypeView(value as "boarding" | "day")}>
                <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                  <TabsTrigger 
                    value="boarding"
                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 dark:text-gray-300 font-semibold"
                  >
                    Boarding School Fees
                  </TabsTrigger>
                  <TabsTrigger 
                    value="day"
                    className="data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=active]:shadow-md text-gray-700 dark:text-gray-300 font-semibold"
                  >
                    Day School Fees
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}
          
          <div className="space-y-6">
            {levelFeesData.map((level) => {
              const isEditingThis = isEditing && editingData?.level === level.level;
              
              // Check if fee data exists for the selected view
              const hasDaySchoolFees = level.categories.some(cat => 
                cat.daySchoolTerm1 !== undefined || cat.daySchoolTerm2 !== undefined || cat.daySchoolTerm3 !== undefined
              );
              const hasBoardingFees = level.categories.some(cat => 
                cat.term1 !== undefined && cat.term1 !== null
              );
              
              const showNoDataMessage = schoolType === "Mixed (Day & Boarding)" && (
                (feeTypeView === "day" && !hasDaySchoolFees) ||
                (feeTypeView === "boarding" && !hasBoardingFees)
              );
              
              const t1 = calculateTermTotal(level.categories, "term1", feeTypeView);
              const t2 = calculateTermTotal(level.categories, "term2", feeTypeView);
              const t3 = calculateTermTotal(level.categories, "term3", feeTypeView);
              const annualTotal = t1 + t2 + t3;

              return (
                <Card key={level.level} className="m-3 border-2 border-gray-300 dark:border-gray-600 shadow-md bg-white dark:bg-gray-800">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-200 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">{level.level}</CardTitle>
                      {schoolType === "Mixed (Day & Boarding)" && !showNoDataMessage && (
                        <Badge 
                          variant="outline" 
                          className={`text-sm font-semibold ${
                            feeTypeView === "boarding" 
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" 
                              : "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                          }`}
                        >
                          {feeTypeView === "boarding" ? "Boarding Fees" : "Day School Fees"}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 bg-white dark:bg-gray-800">
                {!isEditingThis ? (
                  <>
                    <div className="flex justify-end mb-3 gap-2">
                      <Button
                        onClick={() => handleDelete(level)}
                        size="sm"
                        variant="outline"
                        className="gap-2 border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        disabled={deleting && deletingId === level.feeStructureUid}
                      >
                        {deleting && deletingId === level.feeStructureUid ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          <>
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleEdit(level)}
                        size="sm"
                        variant="outline"
                        className="gap-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                      >
                        <Edit2 className="h-4 w-4" />
                        Edit Fee Structure
                      </Button>
                    </div>
                    
                    {/* Show message if fee data not recorded for selected type */}
                    {showNoDataMessage ? (
                      <div className="p-8 text-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-lg">
                        <div className="flex flex-col items-center gap-4">
                          <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-4">
                            <AlertTriangle className="h-12 w-12 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-amber-900 dark:text-amber-200 mb-2">
                              {feeTypeView === "day" ? "Day School Fees" : "Boarding School Fees"} Not Recorded
                            </h3>
                            <p className="text-amber-800 dark:text-amber-300">
                              Fee structure for {feeTypeView === "day" ? "day school students" : "boarding school students"} has not been recorded for {level.level}.
                            </p>
                            <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">
                              Please update the fee structure to include {feeTypeView === "day" ? "day school" : "boarding school"} fees.
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <div className="overflow-x-auto border-2 border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-gray-700 dark:to-gray-800 border-b-2 border-gray-300 dark:border-gray-600">
                        <TableHead className="w-[200px] text-gray-900 dark:text-gray-100 font-bold">Fee Category</TableHead>
                        <TableHead className="text-right text-gray-900 dark:text-gray-100 font-bold">Term 1</TableHead>
                        <TableHead className="text-right text-gray-900 dark:text-gray-100 font-bold">Term 2</TableHead>
                        <TableHead className="text-right text-gray-900 dark:text-gray-100 font-bold">Term 3</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {level.categories.map((cat, i) => {
                        const displayTerm1 = feeTypeView === "day" ? (cat.daySchoolTerm1 ?? cat.term1) : cat.term1;
                        const displayTerm2 = feeTypeView === "day" ? (cat.daySchoolTerm2 ?? cat.term2) : cat.term2;
                        const displayTerm3 = feeTypeView === "day" ? (cat.daySchoolTerm3 ?? cat.term3) : cat.term3;
                        
                        // Smart display: show fee if recorded, otherwise show dash for cleaner look
                        const formatSmartCurrency = (amount: number | undefined) => {
                          if (amount === undefined || amount === null) return "-";
                          return formatCurrency(amount);
                        };
                        
                        return (
                        <TableRow key={i} className="hover:bg-blue-50 dark:hover:bg-gray-700/70 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                          <TableCell className="font-medium text-gray-900 dark:text-white">
                            {cat.name}
                            {cat.paidOnce && (
                              <Badge variant="outline" className="ml-2 text-xs border-purple-500 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-semibold">
                                Paid Once
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-gray-900 dark:text-white">{formatSmartCurrency(displayTerm1)}</TableCell>
                          <TableCell className="text-right text-gray-900 dark:text-white">{formatSmartCurrency(displayTerm2)}</TableCell>
                          <TableCell className="text-right text-gray-900 dark:text-white">{formatSmartCurrency(displayTerm3)}</TableCell>
                        </TableRow>
                        );
                      })}
                      {/* Only show totals if there's actual fee data */}
                      {!showNoDataMessage && (
                        <>
                      <TableRow className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 font-bold border-t-2 border-blue-300 dark:border-blue-700">
                        <TableCell className="text-blue-700 dark:text-blue-300 font-bold">TERM TOTAL</TableCell>
                        <TableCell className="text-right text-blue-700 dark:text-blue-300 font-bold">
                          {formatCurrency(t1)}
                        </TableCell>
                        <TableCell className="text-right text-blue-700 dark:text-blue-300 font-bold">
                          {formatCurrency(t2)}
                        </TableCell>
                        <TableCell className="text-right text-blue-700 dark:text-blue-300 font-bold">
                          {formatCurrency(t3)}
                        </TableCell>
                      </TableRow>
                      <TableRow className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 font-bold border-t-2 border-green-300 dark:border-green-700">
                        <TableCell className="text-green-700 dark:text-green-300 font-bold">ANNUAL TOTAL</TableCell>
                        <TableCell className="text-right text-green-700 dark:text-green-300 font-bold" colSpan={3}>
                          {formatCurrency(annualTotal)}
                        </TableCell>
                      </TableRow>
                      {level.isGovernmentSponsored && level.governmentSponsorshipAmount && level.governmentSponsorshipAmount > 0 && annualTotal > 0 && (
                        <>
                          <TableRow className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 font-semibold border-t-2 border-amber-300 dark:border-amber-700">
                            <TableCell className="text-amber-700 dark:text-amber-300 font-bold">GOVERNMENT SPONSORSHIP</TableCell>
                            <TableCell className="text-right text-amber-700 dark:text-amber-300 font-bold" colSpan={3}>
                              - {formatCurrency(level.governmentSponsorshipAmount)}
                            </TableCell>
                          </TableRow>
                          <TableRow className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 font-bold border-t-2 border-purple-300 dark:border-purple-700">
                            <TableCell className="text-purple-700 dark:text-purple-300 font-bold">NET AMOUNT PAYABLE</TableCell>
                            <TableCell className="text-right text-purple-700 dark:text-purple-300 font-bold" colSpan={3}>
                              {formatCurrency(
                                Math.max(0, annualTotal - level.governmentSponsorshipAmount)
                              )}
                            </TableCell>
                          </TableRow>
                        </>
                      )}
                        </>
                      )}
                    </TableBody>
                  </Table>
                </div>
                )}
                {level.feeType === "uniform" && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-2 border-blue-300 dark:border-blue-700 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-blue-900 dark:text-blue-200">Uniform Fee Structure</p>
                        <p className="text-sm text-blue-800 dark:text-blue-300 mt-1">
                          This fee structure applies uniformly to all students across all classes and streams in the school.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                  </>
                ) : editingData && (
                  <>
                    <div className="space-y-4 mb-4">
                      <div className="flex justify-between items-center p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border-2 border-blue-300 dark:border-blue-700">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Edit Fee Structure</h3>
                          {schoolType === "Mixed (Day & Boarding)" && (
                            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                              Toggle between Boarding and Day School tabs above to edit both fee types
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={handleCancelEdit}
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            disabled={saving}
                          >
                            <X className="h-4 w-4" />
                            Cancel
                          </Button>
                          <Button
                            onClick={handleSaveEdit}
                            size="sm"
                            className="gap-2 bg-green-600 hover:bg-green-700"
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save className="h-4 w-4" />
                                Save Changes
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {editingData.categories.map((cat, index) => (
                        <Card key={index} className="border-2 border-blue-300 dark:border-blue-700 bg-gradient-to-br from-white to-blue-50 dark:from-gray-800 dark:to-gray-900 mb-4">
                          <CardContent className="pt-6 space-y-4">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Category Name</Label>
                              <Input
                                value={cat.name}
                                onChange={(e) => updateCategoryField(index, "name", e.target.value)}
                                className="bg-white dark:bg-gray-800"
                              />
                            </div>
                            
                            {/* Show appropriate fee type label */}
                            {schoolType === "Mixed (Day & Boarding)" && (
                              <div className="mb-2">
                                <Badge 
                                  variant="outline" 
                                  className={`${
                                    feeTypeView === "boarding" 
                                      ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300" 
                                      : "border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                  }`}
                                >
                                  {feeTypeView === "boarding" ? "Boarding School Fees" : "Day School Fees"}
                                </Badge>
                              </div>
                            )}
                            
                            {feeTypeView === "boarding" ? (
                              // Boarding School Fees
                              <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Boarding Term 1 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.term1 || ""}
                                    onChange={(e) => updateCategoryField(index, "term1", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Boarding Term 2 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.term2 || ""}
                                    onChange={(e) => updateCategoryField(index, "term2", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-blue-700 dark:text-blue-300">Boarding Term 3 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.term3 || ""}
                                    onChange={(e) => updateCategoryField(index, "term3", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                              </div>
                            ) : (
                              // Day School Fees
                              <div className="grid grid-cols-3 gap-4">
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">Day School Term 1 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.daySchoolTerm1 || ""}
                                    onChange={(e) => updateCategoryField(index, "daySchoolTerm1", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">Day School Term 2 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.daySchoolTerm2 || ""}
                                    onChange={(e) => updateCategoryField(index, "daySchoolTerm2", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label className="text-sm font-medium text-green-700 dark:text-green-300">Day School Term 3 (KSH)</Label>
                                  <Input
                                    type="number"
                                    value={cat.daySchoolTerm3 || ""}
                                    onChange={(e) => updateCategoryField(index, "daySchoolTerm3", e.target.value)}
                                    className="bg-white dark:bg-gray-800"
                                  />
                                </div>
                              </div>
                            )}
                            
                            <div className="flex items-center space-x-2">
                              <Checkbox
                                id={`edit-paid-once-${index}`}
                                checked={cat.paidOnce || false}
                                onCheckedChange={(checked) => updateCategoryField(index, "paidOnce", checked === true)}
                              />
                              <Label htmlFor={`edit-paid-once-${index}`} className="text-sm cursor-pointer">
                                One-time fee (paid only in Term 1)
                              </Label>
                            </div>
                          </CardContent>
                        </Card>
                      ))}

                      <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Information</h3>
                        
                        {/* Insights Editor */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Insights</Label>
                            <div className="space-y-2">
                                {editingInsightsList.length > 0 && (
                                    <ul className="space-y-2 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-md">
                                        {editingInsightsList.map((item, idx) => (
                                            <li key={idx} className="flex items-start justify-between text-sm gap-2">
                                                {editingInsightIndex === idx ? (
                                                    <div className="flex-1 flex items-center gap-2">
                                                        <Input
                                                            value={editingInsightValue}
                                                            onChange={(e) => setEditingInsightValue(e.target.value)}
                                                            className="flex-1 h-7 text-sm"
                                                            onKeyDown={(e) => { if(e.key === 'Enter') saveEditInsight(); if(e.key === 'Escape') cancelEditInsight(); }}
                                                            autoFocus
                                                        />
                                                        <Button onClick={saveEditInsight} size="sm" variant="ghost" className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50">
                                                            <Save className="h-3 w-3" />
                                                        </Button>
                                                        <Button onClick={cancelEditInsight} size="sm" variant="ghost" className="h-7 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                                                            <X className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="text-gray-700 dark:text-gray-300 flex-1">• {item}</span>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => startEditInsight(idx, item)} className="text-blue-500 hover:text-blue-700 p-1">
                                                                <Edit2 className="h-3 w-3" />
                                                            </button>
                                                            <button onClick={() => removeEditingInsight(idx)} className="text-red-500 hover:text-red-700 p-1">
                                                                <Trash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="flex gap-2">
                                    <Input
                                        value={currentEditingInsight}
                                        onChange={(e) => setCurrentEditingInsight(e.target.value)}
                                        placeholder="Add insight..."
                                        className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 h-8 text-sm"
                                        onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addEditingInsight(); } }}
                                    />
                                    <Button onClick={addEditingInsight} size="sm" variant="secondary" className="h-8">
                                        Add
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Notes Editor */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Important Notes</Label>
                            <div className="space-y-2">
                                {editingNotesList.length > 0 && (
                                    <ul className="space-y-2 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-md">
                                        {editingNotesList.map((item, idx) => (
                                            <li key={idx} className="flex items-start justify-between text-sm gap-2">
                                                {editingNoteIndex === idx ? (
                                                    <div className="flex-1 flex items-center gap-2">
                                                        <Input
                                                            value={editingNoteValue}
                                                            onChange={(e) => setEditingNoteValue(e.target.value)}
                                                            className="flex-1 h-7 text-sm"
                                                            onKeyDown={(e) => { if(e.key === 'Enter') saveEditNote(); if(e.key === 'Escape') cancelEditNote(); }}
                                                            autoFocus
                                                        />
                                                        <Button onClick={saveEditNote} size="sm" variant="ghost" className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50">
                                                            <Save className="h-3 w-3" />
                                                        </Button>
                                                        <Button onClick={cancelEditNote} size="sm" variant="ghost" className="h-7 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                                                            <X className="h-3 w-3" />
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <span className="text-gray-700 dark:text-gray-300 flex-1">• {item}</span>
                                                        <div className="flex items-center gap-1">
                                                            <button onClick={() => startEditNote(idx, item)} className="text-blue-500 hover:text-blue-700 p-1">
                                                                <Edit2 className="h-3 w-3" />
                                                            </button>
                                                            <button onClick={() => removeEditingNote(idx)} className="text-red-500 hover:text-red-700 p-1">
                                                                <Trash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <div className="flex gap-2">
                                    <Input
                                        value={currentEditingNote}
                                        onChange={(e) => setCurrentEditingNote(e.target.value)}
                                        placeholder="Add important note..."
                                        className="bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 h-8 text-sm"
                                        onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addEditingNote(); } }}
                                    />
                                    <Button onClick={addEditingNote} size="sm" variant="secondary" className="h-8">
                                        Add
                                    </Button>
                                </div>
                            </div>
                        </div>

                         <div className="flex items-center space-x-2 pt-2">
                          <Checkbox
                            id="edit-government-sponsored"
                            checked={editingData.isGovernmentSponsored || false}
                            onCheckedChange={(checked) => setEditingData({ ...editingData, isGovernmentSponsored: checked === true })}
                          />
                          <Label htmlFor="edit-government-sponsored" className="text-sm cursor-pointer">
                            Government Sponsored
                          </Label>
                        </div>
                        {editingData.isGovernmentSponsored && (
                          <div className="space-y-2">
                            <Label className="text-sm font-medium">Government Sponsorship Amount (KSH)</Label>
                            <Input
                              type="number"
                              value={editingData.governmentSponsorshipAmount || 0}
                              onChange={(e) => setEditingData({ ...editingData, governmentSponsorshipAmount: Number(e.target.value) })}
                              className="bg-white dark:bg-gray-800"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
        </CardContent>
      </Card>

      {/* Global Insights and Notes Section - Editable once for all levels */}
      {!isEditingGlobalNotes && !globalInsights && !globalNotes && levelFeesData.length > 0 && (
        <Card className="m-2 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg shadow">
          <CardContent className="p-6 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="rounded-full bg-blue-100 dark:bg-blue-900/30 p-4">
                <svg className="h-12 w-12 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Add Fee Structure Insights & Notes</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Help parents understand the fee structure by adding insights and important notes
                </p>
                <Button
                  onClick={handleEditGlobalNotes}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                >
                  <Edit2 className="h-4 w-4" />
                  Add Insights & Notes
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {globalInsights && !isEditingGlobalNotes && (
        <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
          <CardHeader className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 flex flex-row items-start justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Fee Structure Insights</CardTitle>
              <Badge variant="secondary" className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-none">Analysis</Badge>
            </div>
            <Button
              onClick={handleEditGlobalNotes}
              size="sm"
              variant="outline"
              className="gap-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-3">
              {globalInsights.split('\n').map((line, index) => (
                line.trim() && (
                  <div key={index} className="m-2 flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400 mt-2 flex-shrink-0"></div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{line}</p>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {globalNotes && !isEditingGlobalNotes && (
        <Card className="m-2 bg-gray-50 dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-600">
          <CardHeader className="border-b border-gray-200 dark:border-gray-600 flex flex-row items-start justify-between">
            <CardTitle className="font-semibold text-gray-900 dark:text-white">Important Notes</CardTitle>
            <Button
              onClick={handleEditGlobalNotes}
              size="sm"
              variant="outline"
              className="gap-2 border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
            >
              <Edit2 className="h-4 w-4" />
              Edit
            </Button>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="list-disc list-inside space-y-2 ml-2 text-sm text-gray-700 dark:text-gray-300">
              {globalNotes.split('\n').map((line, index) => (
                line.trim() && <li key={index}>{line}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Edit Mode for Global Insights and Notes */}
      {isEditingGlobalNotes && (
        <Card className="m-2 bg-white dark:bg-gray-800 rounded-lg shadow border-2 border-blue-500 dark:border-blue-700">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-b-2 border-blue-300 dark:border-blue-700">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl font-bold text-gray-900 dark:text-white">Edit Fee Structure Details</CardTitle>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">These details apply to all fee structures globally</p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleCancelGlobalNotesEdit}
                  size="sm"
                  variant="outline"
                  className="gap-2"
                  disabled={savingGlobalNotes}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveGlobalNotes}
                  size="sm"
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  disabled={savingGlobalNotes}
                >
                  {savingGlobalNotes ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Global Insights Editor */}
            <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                    <span>Fee Structure Insights</span>
                    <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">Global</Badge>
                </Label>
                <div className="space-y-2">
                    {editingGlobalInsightsList.length > 0 && (
                        <ul className="space-y-2 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-md border border-gray-200 dark:border-gray-700">
                            {editingGlobalInsightsList.map((item, idx) => (
                                <li key={idx} className="flex items-start justify-between text-sm gap-2">
                                    {editingGlobalInsightIndex === idx ? (
                                        <div className="flex-1 flex items-center gap-2">
                                            <Input
                                                value={editingGlobalInsightValue}
                                                onChange={(e) => setEditingGlobalInsightValue(e.target.value)}
                                                className="flex-1 h-8 text-sm"
                                                onKeyDown={(e) => { if(e.key === 'Enter') saveEditGlobalInsight(); if(e.key === 'Escape') cancelEditGlobalInsight(); }}
                                                autoFocus
                                            />
                                            <Button onClick={saveEditGlobalInsight} size="sm" variant="ghost" className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50">
                                                <Save className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button onClick={cancelEditGlobalInsight} size="sm" variant="ghost" className="h-8 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-gray-700 dark:text-gray-300 flex-1">• {item}</span>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => startEditGlobalInsight(idx, item)} className="text-blue-500 hover:text-blue-700 p-1">
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => removeGlobalInsight(idx)} className="text-red-500 hover:text-red-700 p-1">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="flex gap-2">
                        <Input
                            value={currentGlobalInsight}
                            onChange={(e) => setCurrentGlobalInsight(e.target.value)}
                            placeholder="Add insight..."
                            className="bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 h-9"
                            onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addGlobalInsight(); } }}
                        />
                        <Button onClick={addGlobalInsight} size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 text-white">
                            Add
                        </Button>
                    </div>
                </div>
            </div>

            {/* Global Notes Editor */}
            <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                    <span>Important Notes</span>
                     <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">Global</Badge>
                </Label>
                <div className="space-y-2">
                    {editingGlobalNotesList.length > 0 && (
                        <ul className="space-y-2 bg-gray-50 dark:bg-gray-800/50 p-2 rounded-md border border-gray-200 dark:border-gray-700">
                            {editingGlobalNotesList.map((item, idx) => (
                                <li key={idx} className="flex items-start justify-between text-sm gap-2">
                                    {editingGlobalNoteIndex === idx ? (
                                        <div className="flex-1 flex items-center gap-2">
                                            <Input
                                                value={editingGlobalNoteValue}
                                                onChange={(e) => setEditingGlobalNoteValue(e.target.value)}
                                                className="flex-1 h-8 text-sm"
                                                onKeyDown={(e) => { if(e.key === 'Enter') saveEditGlobalNote(); if(e.key === 'Escape') cancelEditGlobalNote(); }}
                                                autoFocus
                                            />
                                            <Button onClick={saveEditGlobalNote} size="sm" variant="ghost" className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50">
                                                <Save className="h-3.5 w-3.5" />
                                            </Button>
                                            <Button onClick={cancelEditGlobalNote} size="sm" variant="ghost" className="h-8 px-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100">
                                                <X className="h-3.5 w-3.5" />
                                            </Button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-gray-700 dark:text-gray-300 flex-1">• {item}</span>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => startEditGlobalNote(idx, item)} className="text-blue-500 hover:text-blue-700 p-1">
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => removeGlobalNote(idx)} className="text-red-500 hover:text-red-700 p-1">
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="flex gap-2">
                        <Input
                            value={currentGlobalNote}
                            onChange={(e) => setCurrentGlobalNote(e.target.value)}
                            placeholder="Add note..."
                            className="bg-white dark:bg-gray-800 border-2 border-blue-300 dark:border-blue-700 h-9"
                            onKeyDown={(e) => { if(e.key === 'Enter') { e.preventDefault(); addGlobalNote(); } }}
                        />
                        <Button onClick={addGlobalNote} size="sm" className="h-9 bg-blue-600 hover:bg-blue-700 text-white">
                            Add
                        </Button>
                    </div>
                </div>
            </div>
            </CardContent>
        </Card>
      )}
    </div>
  );
}